/**
 * Client-side identity + capability hook.
 *
 * Fetches /api/me once on mount and exposes `useMe()` so any component
 * can check `me.can('view:user_management')` to gate UI elements.
 * In production this dedup-fetches; in mock mode it lands instantly.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import type { Capability } from './roles';

interface Me {
  email: string;
  name: string | null;
  roles: string[];
  capabilities: Capability[];
}

interface MeContextValue {
  me: Me | null;
  loading: boolean;
  error: string | null;
  can: (cap: Capability) => boolean;
  hasRole: (role: string) => boolean;
  /** Re-pull /api/me (e.g. after a role grant). See #1390. */
  refetch: () => void;
}

const MeContext = createContext<MeContextValue>({
  me: null,
  loading: true,
  error: null,
  can: () => false,
  hasRole: () => false,
  refetch: () => {},
});

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(() => {
    return apiFetch<Me>('/api/me')
      .then((d) => {
        setMe(d);
        setError(null);
      })
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchMe();
    // #1390: re-pull identity when the tab regains focus so a role granted while
    // the app was open (e.g. an admin just provisioned this user) reflects
    // without a full sign-out/in. Pairs with the no-store header on /api/me.
    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchMe();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchMe]);

  const can = (cap: Capability) => Boolean(me?.capabilities.includes(cap));
  const hasRole = (role: string) => Boolean(me?.roles.includes(role));

  return (
    <MeContext.Provider
      value={{ me, loading, error, can, hasRole, refetch: fetchMe }}
    >
      {children}
    </MeContext.Provider>
  );
}

export function useMe(): MeContextValue {
  return useContext(MeContext);
}
